import { moduleByKey } from "./moduleCatalog";
import { moduleNarrativeFor } from "./modulePageNarrative";
import { moduleProfileFor } from "./moduleProfiles";
import { productEvidenceForModule } from "./productEvidence";

// Closed presentation taxonomy for the buyer-facing "Example view" stage. A new catalogue kind
// must be deliberately mapped here; unknown records never fall through to a generic dashboard.
export const SUPPORTED_MODULE_EXAMPLE_KINDS = Object.freeze([
  "dashboard",
  "workbench",
  "document",
  "report",
  "site",
  "mobile",
  "provider",
  "device",
  "automation",
  "ai",
  "timetable-grid",
  "exam-datesheet",
  "lesson-document",
  "form-builder",
  "payroll-run",
  "export-table",
  "finance-ledger",
]);

const CATALOGUE_KIND_TO_EXAMPLE_KIND = Object.freeze({
  "ERP module": "workbench",
  "ERP feature": "workbench",
  "Report & analytics": "report",
  "Provider integration": "provider",
  "Device integration": "device",
  "Mobile experience": "mobile",
  "Workflow automation": "automation",
  "Governed AI capability": "ai",
});

const SPECIAL_EXAMPLE_KIND_BY_KEY = Object.freeze({
  "website-builder-ai": "site",
  "timetable-management": "timetable-grid",
  "exam-scheduling": "exam-datesheet",
  "lesson-plan-management": "lesson-document",
  "custom-forms": "form-builder",
  payroll: "payroll-run",
  "export-excel": "export-table",
  "fee-collection": "finance-ledger",
  "online-payment": "finance-ledger",
  "payment-gateway": "finance-ledger",
  "pending-fee-tracking": "finance-ledger",
  "refund-management": "finance-ledger",
  "result-publishing": "document",
  "certificate-generation": "document",
  "id-card-management": "document",
  "receipt-generation": "document",
  "export-pdf": "document",
  "report-card-ai": "document",
  "financial-summary": "dashboard",
  "dashboard-analytics": "dashboard",
  "performance-insights": "dashboard",
  "real-time-charts": "dashboard",
  "daily-summary": "dashboard",
});

const SPECIAL_PRESENTATION_BY_KEY = Object.freeze({
  "timetable-management": Object.freeze({
    title: "Weekly timetable proof",
    labels: Object.freeze(["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"]),
    sampleItems: Object.freeze(["Mathematics", "Science", "English", "Library", "Physical education"]),
  }),
  "exam-scheduling": Object.freeze({
    title: "Exam datesheet proof",
    labels: Object.freeze(["Assessment", "Class scope", "Date and session", "Maximum / pass marks"]),
    sampleItems: Object.freeze(["English assessment", "Mathematics assessment", "Science assessment"]),
  }),
  "lesson-plan-management": Object.freeze({
    title: "Lesson plan proof",
    labels: Object.freeze(["Learning objective", "Class activity", "Resource", "Teacher review"]),
    sampleItems: Object.freeze(["Teacher-defined objective", "Planned class activity", "Selected learning resource", "Teacher review note"]),
  }),
  "custom-forms": Object.freeze({
    title: "Form builder and response proof",
    labels: Object.freeze(["Field builder", "Published form", "Submitted response", "Authorised review"]),
    sampleItems: Object.freeze(["Parent or guardian name", "Class seeking admission", "Supporting document"]),
  }),
  payroll: Object.freeze({
    title: "Payroll run and payslip proof",
    labels: Object.freeze(["Employee record", "Earnings", "Deductions", "Net pay and payslip"]),
    sampleItems: Object.freeze(["Sample employee record", "Earnings field", "Deduction field", "Payslip field"]),
  }),
  "export-excel": Object.freeze({
    title: "Filter-aware spreadsheet proof",
    labels: Object.freeze(["Selected report", "Applied filters", "Export columns", "Audit evidence"]),
    sampleItems: Object.freeze(["Sample learner record", "Sample staff record", "Sample fee record"]),
  }),
  "fee-collection": Object.freeze({
    title: "Invoice and collection proof",
    labels: Object.freeze(["Issued invoice", "Recorded collection", "Receipt evidence", "Finance review"]),
    sampleItems: Object.freeze(["Sample invoice field", "Sample collection field", "Sample receipt field", "Review field"]),
  }),
  "online-payment": Object.freeze({
    title: "Online payment proof",
    labels: Object.freeze(["Invoice-bound order", "Provider checkout", "Confirmed provider evidence", "Invoice update"]),
    sampleItems: Object.freeze(["Sample invoice reference", "Provider checkout state", "Provider evidence field", "Invoice status field"]),
  }),
  "payment-gateway": Object.freeze({
    title: "Gateway evidence proof",
    labels: Object.freeze(["School gateway", "Invoice-bound order", "Webhook or provider verification", "Reconciliation evidence"]),
    sampleItems: Object.freeze(["Configured gateway field", "Sample order reference", "Provider verification field", "Reconciliation evidence field"]),
  }),
  "pending-fee-tracking": Object.freeze({
    title: "Outstanding-fee review proof",
    labels: Object.freeze(["Issued demand", "Recorded collection", "Current balance", "Authorised follow-up"]),
    sampleItems: Object.freeze(["Sample demand field", "Collection evidence field", "Balance field", "Follow-up review field"]),
  }),
  "refund-management": Object.freeze({
    title: "Refund review proof",
    labels: Object.freeze(["Original collection", "Refund request", "Authorised action", "Provider or ledger evidence"]),
    sampleItems: Object.freeze(["Original collection field", "Sample refund request", "Authorisation field", "Provider or ledger evidence field"]),
  }),
});

const DOCUMENT_PRESENTATION_BY_KEY = Object.freeze({
  "result-publishing": Object.freeze({ type: "report-card", title: "Published result preview", review: "Board configuration and publication state remain visible" }),
  "report-card-ai": Object.freeze({ type: "report-card", title: "Teacher-reviewed report card", review: "AI-drafted remarks remain held for teacher approval" }),
  "certificate-generation": Object.freeze({ type: "certificate", title: "Certificate proof", review: "Issue details remain reviewable before generation" }),
  "id-card-management": Object.freeze({ type: "identity-card", title: "Identity card proof", review: "Student identity fields remain reviewable before print" }),
  "receipt-generation": Object.freeze({ type: "receipt", title: "Payment receipt proof", review: "Receipt output remains tied to the recorded collection" }),
  "export-pdf": Object.freeze({ type: "print-output", title: "Printable document proof", review: "The selected school record determines the exported document" }),
});

const EXAMPLE_COPY_BY_KIND = Object.freeze({
  dashboard: Object.freeze({ state: "View state", output: "Signal shown", outcome: "Decision-ready result" }),
  workbench: Object.freeze({ state: "Record state", output: "Supported action", outcome: "Operational result" }),
  document: Object.freeze({ state: "Document state", output: "Printable output", outcome: "Issued result" }),
  report: Object.freeze({ state: "Report scope", output: "Evidence shown", outcome: "Reviewable result" }),
  site: Object.freeze({ state: "Preview state", output: "Published experience", outcome: "School outcome" }),
  mobile: Object.freeze({ state: "App state", output: "Action shown", outcome: "User outcome" }),
  provider: Object.freeze({ state: "Delivery state", output: "Provider evidence", outcome: "Traceable result" }),
  device: Object.freeze({ state: "Device event", output: "Accepted record", outcome: "Operational result" }),
  automation: Object.freeze({ state: "Rule state", output: "Run evidence", outcome: "Controlled result" }),
  ai: Object.freeze({ state: "Assistant state", output: "Review boundary", outcome: "Governed result" }),
  "timetable-grid": Object.freeze({ state: "Schedule state", output: "Weekly timetable", outcome: "Scheduling result" }),
  "exam-datesheet": Object.freeze({ state: "Datesheet state", output: "Exam schedule", outcome: "Schedule result" }),
  "lesson-document": Object.freeze({ state: "Planning state", output: "Lesson plan", outcome: "Reviewable plan" }),
  "form-builder": Object.freeze({ state: "Form state", output: "Published form and response", outcome: "Collection result" }),
  "payroll-run": Object.freeze({ state: "Payroll state", output: "Run and payslip", outcome: "Payroll result" }),
  "export-table": Object.freeze({ state: "Export state", output: "Spreadsheet output", outcome: "Reviewable result" }),
  "finance-ledger": Object.freeze({ state: "Finance state", output: "Payment evidence", outcome: "Reviewable finance result" }),
});

const DEMO_SCHOOL_SITE = Object.freeze({
  name: "Vidyapeeth Demo School",
  disclosure: "Generated fictional campus visual · not a real institution, customer website or live-product screen",
  nav: Object.freeze(["About", "Academics", "Admissions", "Campus life"]),
  eyebrow: "Admissions open · Demo content",
  headline: "A welcoming school website, ready for your team to review.",
  supporting: "Present academics, admissions and school life clearly, then route enabled enquiry forms into the admissions workflow.",
  action: "Explore admissions",
  image: Object.freeze({
    src: "/marketing/module-examples/demo-school-campus-v1.webp",
    srcSet: [
      "/marketing/module-examples/demo-school-campus-v1.w480.webp 480w",
      "/marketing/module-examples/demo-school-campus-v1.w768.webp 768w",
      "/marketing/module-examples/demo-school-campus-v1.w1080.webp 1080w",
      "/marketing/module-examples/demo-school-campus-v1.webp 1568w",
    ].join(", "),
    sizes: "(max-width: 639px) calc(100vw - 40px), (max-width: 1199px) 68vw, 900px",
    width: 1568,
    height: 1003,
    alt: "Generated fictional Indian school campus used only to illustrate the visual direction of a demo-school website; it does not depict a real institution or customer.",
    sha256: "11969335ed61f95ed9c5e4de87295517569c39f23ae33aa92bb449d37d411857",
  }),
});

const freezeList = (values) => Object.freeze((values || []).filter(Boolean));

function canonicalModule(moduleOrKey) {
  const key = typeof moduleOrKey === "string" ? moduleOrKey : moduleOrKey?.key;
  return key ? moduleByKey[key] || null : null;
}

export function moduleExampleKindFor(moduleOrKey) {
  const mod = canonicalModule(moduleOrKey);
  if (!mod) return null;

  const special = SPECIAL_EXAMPLE_KIND_BY_KEY[mod.key];
  if (special) return special;

  // Aira capabilities keep the governed-AI treatment even if their catalogue kind is later
  // presented through an operational workspace. The document/site exceptions above win first.
  if (mod.group === "aira" || mod.crossWorkspaceLayer === "aira") return "ai";

  return CATALOGUE_KIND_TO_EXAMPLE_KIND[mod.kind] || null;
}

/**
 * Derive a display profile exclusively from the reviewed module catalogue, narrative and profile.
 * This adapter owns presentation labels, not product claims or sample metrics.
 */
export function moduleExampleProfileFor(moduleOrKey, suppliedProfile, suppliedNarrative) {
  const mod = canonicalModule(moduleOrKey);
  if (!mod) return null;

  const kind = moduleExampleKindFor(mod);
  const labels = EXAMPLE_COPY_BY_KIND[kind];
  if (!kind || !labels) return null;

  const profile = suppliedProfile || moduleProfileFor(mod);
  const narrative = suppliedNarrative || moduleNarrativeFor(mod, profile);
  if (!profile || !narrative) return null;

  const capabilities = freezeList(narrative.capabilities || mod.detail?.bullets);
  const evaluationLenses = freezeList(narrative.steps);
  const roles = freezeList(profile.roles);
  const prerequisites = freezeList(profile.prerequisites);
  const outputValue = capabilities[0];

  // Refuse an incomplete profile instead of filling the visual with generic marketing language.
  if (!mod.oneLiner || !outputValue || !profile.outcome || !evaluationLenses.length) return null;

  const isSyntheticSite = mod.key === "website-builder-ai";
  const productEvidence = productEvidenceForModule(mod.key);

  const result = {
    key: mod.key,
    moduleName: mod.name,
    kind,
    workspace: mod.workspaceTitle || mod.groupTitle,
    previewLabel: `${mod.name} example view`,
    previewTitle: `${mod.name} in a school workflow`,
    // Keep the product example visually dominant. The full reviewed scope remains immediately
    // below in the buyer brief; this header uses the concise, route-specific catalogue promise.
    previewSummary: mod.oneLiner,
    state: Object.freeze({ label: labels.state, value: mod.oneLiner, source: "module.oneLiner" }),
    output: Object.freeze({ label: labels.output, value: outputValue, source: "module.detail.bullets[0]" }),
    outcome: Object.freeze({ label: labels.outcome, value: profile.outcome, source: "moduleProfile.outcome" }),
    items: Object.freeze(capabilities.slice(0, 4)),
    evaluationLenses,
    roles,
    prerequisites,
    aiBoundary: narrative.aiSupport || null,
    document: DOCUMENT_PRESENTATION_BY_KEY[mod.key] || null,
    presentation: SPECIAL_PRESENTATION_BY_KEY[mod.key] || null,
    site: isSyntheticSite ? DEMO_SCHOOL_SITE : null,
    productEvidenceId: productEvidence?.id || null,
    sourceType: isSyntheticSite && productEvidence
      ? "illustrative-context-and-first-party-product-evidence"
      : productEvidence
      ? "first-party-product-evidence"
      : "code-native-illustration",
    disclosure: isSyntheticSite && productEvidence
      ? "Generated fictional campus context · not a real institution, customer website or live-product screen · Result: First-party product evidence using deterministic synthetic demo data"
      : productEvidence
      ? "First-party product evidence · deterministic synthetic fixture · no customer data · visibly marked SAMPLE or DEMO"
      : "Code-native illustrative example · not live product UI · no customer data",
  };

  return Object.freeze(result);
}

export const MODULE_EXAMPLE_KIND_OVERRIDES = SPECIAL_EXAMPLE_KIND_BY_KEY;
export const MODULE_EXAMPLE_DOCUMENT_PRESENTATIONS = DOCUMENT_PRESENTATION_BY_KEY;
export const MODULE_EXAMPLE_SPECIAL_PRESENTATIONS = SPECIAL_PRESENTATION_BY_KEY;
export const MODULE_EXAMPLE_DEMO_SCHOOL_SITE = DEMO_SCHOOL_SITE;
